// LiveView resolves a single `phx-hook` name per element — `hookName` in
// phoenix_live_view.esm.js is read straight from the attribute with no
// splitting — but the sheet's scroll container legitimately needs several
// behaviours on that one element:
//
//   * mobile_sheet_gesture measures `scrollTop` on it to decide whether a
//     downward pull is a dismissal or ordinary content scrolling, so it has to
//     sit on the scroller itself;
//   * ScrollToTop resets that same scroller when the selected record changes,
//     so switching clients doesn't inherit the previous one's scroll position;
//   * sheet_modality supplies the dialog behaviours a `<dialog>` would give for
//     free, which this element cannot use because it is an ordinary inline
//     panel above its collapse point.
//
// Composing them here keeps the single-hook constraint from forcing a second
// scroll container, which `requirement-mobile-single-scroll-owner` forbids.
import ScrollToTop from "./scroll_to_top_hook"
import { setupMobileSheetGesture } from "./mobile_sheet_gesture.js"
import { setupSheetModality } from "./sheet_modality.js"

// The drag region the gesture binds to, mirrored from mobile_sheet_gesture so
// this hook can tell whether a patch replaced it.
const dragRegionOf = (el) =>
  el.querySelector("[data-section-region='header']") ||
  el.querySelector(".details-modal-header")

export const ModalSheetScrollOwner = {
  mounted() {
    ScrollToTop.mounted.call(this)
    this.bindGesture()
    this.modality = setupSheetModality(this.el, {
      query: this.el.dataset.overlayQuery || "(max-width: 1023px)",
    })
    this.modality.sync()
  },

  updated() {
    ScrollToTop.updated.call(this)

    // The header is conditional: an account that has gone away renders none, so
    // the gesture mounted with no drag region and stored a no-op. When a
    // healthy account is selected LiveView reuses this element rather than
    // remounting, so without rebinding, pull-to-dismiss would stay dead until a
    // full remount.
    if (dragRegionOf(this.el) !== this.dragRegion) this.bindGesture()

    this.modality.sync()
  },

  bindGesture() {
    if (this.destroyMobileSheetGesture) this.destroyMobileSheetGesture()
    this.dragRegion = dragRegionOf(this.el)
    this.destroyMobileSheetGesture = setupMobileSheetGesture(this.el)
  },

  destroyed() {
    this.destroyMobileSheetGesture()
    this.modality.destroy()
  },
}
